// C:\Users\QI\komondi-dashboard\src\pages\UserDetailsPage.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { ref, onValue, set, update } from "firebase/database";

export default function UserDetailsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [distributorData, setDistributorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    productsCount: 0,
    vendeursCount: 0,
    categoriesCount: 0,
    brandsCount: 0,
    ordersCount: 0
  });
  const [error, setError] = useState(null);
  const [updatingAccountType, setUpdatingAccountType] = useState(false);

  useEffect(() => {
    console.log("Fetching user with ID:", userId);
    
    const userRef = ref(db, `users/${userId}`);
    const unsub = onValue(userRef, (snap) => {
      const data = snap.val();
      console.log("User data fetched:", data);
      
      if (data) {
        const userWithId = { id: userId, ...data };
        setUser(userWithId);
        
        // فقط للمحترفين نذهب لـ distributors
        if (data.userType && data.userType.toLowerCase() === "professional") {
          console.log("Fetching distributor data for professional");
          fetchDistributorData(userId);
        } else {
          console.log("Not a professional, setting loading to false");
          setLoading(false);
        }
      } else {
        console.log("No user data found");
        setError("User not found");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching user:", error);
      setError("Failed to load user data");
      setLoading(false);
    });
    
    return () => unsub();
  }, [userId]);

  // جلب بيانات الـ distributor
  const fetchDistributorData = (distributorId) => {
    console.log("Fetching distributor data for ID:", distributorId);
    
    const distributorRef = ref(db, `distributors/${distributorId}`);
    const unsub = onValue(distributorRef, (snap) => {
      const data = snap.val();
      console.log("Distributor data:", data);
      setDistributorData(data);
      
      // جلب الإحصائيات
      fetchDistributorStats(distributorId);
    }, (error) => {
      console.error("Error fetching distributor data:", error);
      setLoading(false);
    });
    
    return () => unsub && unsub();
  };

  // جلب إحصائيات الـ distributor
  const fetchDistributorStats = (distributorId) => {
    console.log("Fetching stats for distributor:", distributorId);
    
    const statsPromises = [];
    
    // عدد المنتجات
    const productsPromise = new Promise((resolve) => {
      const productsRef = ref(db, `distributors/${distributorId}/products`);
      onValue(productsRef, (snap) => {
        const products = snap.val();
        resolve({ productsCount: products ? Object.keys(products).length : 0 });
      }, { onlyOnce: true });
    });
    
    // عدد البائعين
    const vendeursPromise = new Promise((resolve) => {
      const vendeursRef = ref(db, `distributors/${distributorId}/vendeurs`);
      onValue(vendeursRef, (snap) => {
        const vendeurs = snap.val();
        resolve({ vendeursCount: vendeurs ? Object.keys(vendeurs).length : 0 });
      }, { onlyOnce: true });
    });
    
    // عدد الأصناف
    const categoriesPromise = new Promise((resolve) => {
      const categoriesRef = ref(db, `distributors/${distributorId}/categories`);
      onValue(categoriesRef, (snap) => {
        const categories = snap.val();
        resolve({ categoriesCount: categories ? Object.keys(categories).length : 0 });
      }, { onlyOnce: true });
    });
    
    // عدد البراندات
    const brandsPromise = new Promise((resolve) => {
      const brandsRef = ref(db, `distributors/${distributorId}/brands`);
      onValue(brandsRef, (snap) => {
        const brands = snap.val();
        resolve({ brandsCount: brands ? Object.keys(brands).length : 0 });
      }, { onlyOnce: true });
    });
    
    // عدد الطلبيات
    const ordersPromise = new Promise((resolve) => {
      const ordersRef = ref(db, `distributors/${distributorId}/orders`);
      onValue(ordersRef, (snap) => {
        const orders = snap.val();
        resolve({ ordersCount: orders ? Object.keys(orders).length : 0 });
      }, { onlyOnce: true });
    });
    
    // جمع جميع النتائج
    Promise.all([productsPromise, vendeursPromise, categoriesPromise, brandsPromise, ordersPromise])
      .then((results) => {
        const combinedStats = results.reduce((acc, stat) => ({ ...acc, ...stat }), {});
        console.log("Combined stats:", combinedStats);
        setStats(combinedStats);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching stats:", error);
        setLoading(false);
      });
  };

  // تحديث حالة المستخدم
  const updateUserStatus = async (newStatus) => {
    try {
      const userRef = ref(db, `users/${userId}/active`);
      await set(userRef, newStatus);
      // تحديث الحالة المحلية
      setUser(prev => ({ ...prev, active: newStatus }));
    } catch (error) {
      console.error("Error updating user status:", error);
      alert("Failed to update user status");
    }
  };

  // تحديث accountType
  const updateAccountType = async (newAccountType) => {
    if (!user) return;
    
    setUpdatingAccountType(true);
    try {
      const userRef = ref(db, `users/${userId}`);
      await update(userRef, {
        accountType: newAccountType
      });
      
      // تحديث الحالة المحلية
      setUser(prev => ({ ...prev, accountType: newAccountType }));
      
      alert(`Account type updated to ${newAccountType} successfully!`);
    } catch (error) {
      console.error("Error updating account type:", error);
      alert("Failed to update account type");
    } finally {
      setUpdatingAccountType(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 300
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>⏳</div>
        <h3>Loading user details...</h3>
        <p style={{ color: "#6b7280" }}>User ID: {userId}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: "center",
        background: "#fef2f2",
        borderRadius: 8,
        margin: 20
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>❌</div>
        <h3 style={{ color: "#dc2626" }}>Error</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "10px 20px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            marginTop: 20
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: "center",
        background: "#f3f4f6",
        borderRadius: 8,
        margin: 20
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>👤</div>
        <h3>User Not Found</h3>
        <p>No user found with ID: {userId}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "10px 20px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            marginTop: 20
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const d = new Date(dateString);
      if (isNaN(d)) return dateString;
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // الحصول على حقول إضافية حسب نوع المستخدم
  const getUserSpecificFields = () => {
    if (!user.userType) return [];

    const baseFields = [
      { label: "Email", value: user.email || "-" },
      { label: "Phone", value: user.phone || "-" },
      { label: "Address", value: user.adresse || "-" },
      { label: "User ID", value: userId },
      { label: "Registration Date", value: formatDate(user.registrationDate) },
      { 
        label: "Status", 
        value: (
          <span style={{ 
            color: user.active !== false ? "#16a34a" : "#ef4444",
            fontWeight: "bold"
          }}>
            {user.active !== false ? "Active" : "Inactive"}
          </span>
        ),
        editable: true 
      }
    ];

    switch (user.userType.toLowerCase()) {
      case "professional":
        return [
          ...baseFields,
          { label: "Activity Name", value: user.activityName || "-" },
          { label: "Trade Name", value: user.tradeName || user.name || "-" },
          { label: "Commune", value: user.commune || "-" },
          { label: "Wilaya", value: user.wilaya || "-" },
          { 
            label: "Account Type", 
            value: (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ 
                  color: user.accountType === "public" ? "#16a34a" : "#ef4444",
                  fontWeight: "bold"
                }}>
                  {user.accountType || "public"}
                </span>
                {user.accountType === "public" ? (
                  <button
                    onClick={() => updateAccountType("privé")}
                    disabled={updatingAccountType}
                    style={{
                      padding: "4px 12px",
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    {updatingAccountType ? "Updating..." : "Make Private"}
                  </button>
                ) : (
                  <button
                    onClick={() => updateAccountType("public")}
                    disabled={updatingAccountType}
                    style={{
                      padding: "4px 12px",
                      background: "#16a34a",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    {updatingAccountType ? "Updating..." : "Make Public"}
                  </button>
                )}
              </div>
            )
          },
          // إحصائيات الـ distributor
          { label: "Products Count", value: stats.productsCount },
          { label: "Vendeurs Count", value: stats.vendeursCount },
          { label: "Categories Count", value: stats.categoriesCount },
          { label: "Brands Count", value: stats.brandsCount },
          { label: "Orders Count", value: stats.ordersCount }
        ];
      
      case "vendeur":
        return [
          ...baseFields,
          { label: "Last Name", value: user.lastName || "-" },
          { label: "First Name", value: user.name || user.firstName || "-" },
          { label: "Trade Name", value: user.tradeName || "-" },
          { label: "Commune", value: user.commune || "-" },
          { label: "Wilaya", value: user.wilaya || "-" },
          { label: "Phone", value: user.phone || "-" },
          { label: "Address", value: user.adresse || "-" },
          { label: "Registration Date", value: formatDate(user.registrationDate) }
        ];
      
      case "client":
        return [
          ...baseFields,
          { label: "Full Name", value: `${user.name || ""} ${user.lastName || ""}`.trim() || "-" },
          { label: "Commune", value: user.commune || "-" },
          { label: "Wilaya", value: user.wilaya || "-" },
          { label: "Phone", value: user.phone || "-" },
          { label: "Address", value: user.adresse || "-" },
          { label: "Registration Date", value: formatDate(user.registrationDate) }
        ];
      
      case "admin":
        return [
          ...baseFields,
          { label: "Role", value: user.role || "Administrator" },
          { label: "Permissions", value: user.permissions ? user.permissions.join(", ") : "Full Access" },
          { label: "Last Login", value: formatDate(user.lastLogin) },
          { label: "Login Count", value: user.loginCount || 0 }
        ];
      
      default:
        return baseFields;
    }
  };

  // الحصول على لون حسب نوع المستخدم
  const getUserTypeColor = () => {
    switch (user.userType?.toLowerCase()) {
      case "client": return "#4f46e5";
      case "professional": return "#0891b2";
      case "vendeur": return "#16a34a";
      case "admin": return "#b91c1c";
      default: return "#6b7280";
    }
  };

  const userSpecificFields = getUserSpecificFields();
  const userTypeColor = getUserTypeColor();

  // بطاقات الإحصائيات للـ Professional
  const StatCard = ({ label, value, icon, color, onClick, clickable }) => (
    <div style={{
      background: color,
      color: "white",
      borderRadius: 8,
      padding: "15px",
      textAlign: "center",
      minWidth: 120
    }}>
      <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: "12px", opacity: 0.9 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: "bold", marginTop: 5 }}>{value}</div>
    </div>
  );

  // عرض بيانات المستخدم للمساعدة في التصحيح
  console.log("Rendering user:", user);
  console.log("User type:", user.userType);
  console.log("All user fields:", Object.keys(user));

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      {/* شريط التنقل */}
      <div style={{ display: "flex", alignItems: "center", gap: 15, marginBottom: 30 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "8px 16px",
            background: "#e5e7eb",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5
          }}
        >
          ← Back
        </button>
        <h2 style={{ margin: 0 }}>User Details</h2>

         <button
    onClick={() => navigate(`/send-notification/${userId}`)}
    style={{
      marginLeft: "auto",
      position: "relative",
      background: "#111827",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: 45,
      height: 45,
      fontSize: 20,
      cursor: "pointer"
    }}
  >
    🔔

    {/* 🔴 Badge */}
    {stats.ordersCount > 0 && (
      <span style={{
        position: "absolute",
        top: -5,
        right: -5,
        background: "red",
        color: "white",
        borderRadius: "50%",
        padding: "2px 6px",
        fontSize: 12,
        fontWeight: "bold"
      }}>
        {stats.ordersCount}
      </span>
    )}
  </button>

        <span style={{
          background: userTypeColor,
          color: "white",
          padding: "6px 16px",
          borderRadius: 20,
          fontSize: "14px",
          fontWeight: "500"
        }}>
          {user.userType || "Unknown"} User
        </span>
      </div>

      {/* البطاقة الرئيسية */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "300px 1fr", 
        gap: 30,
        marginBottom: 30 
      }}>
        {/* معلومات المستخدم الأساسية */}
        <div style={{ 
          background: "white", 
          padding: 25, 
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          border: `1px solid ${userTypeColor}20`
        }}>
          <div style={{ textAlign: "center", marginBottom: 25 }}>
            <img
              src={user.imageUrl || "https://via.placeholder.com/120"}
              alt="User"
              width={120}
              height={120}
              style={{ 
                borderRadius: "50%",
                border: `3px solid ${userTypeColor}`,
                marginBottom: 15
              }}
            />
            <h3 style={{ margin: "10px 0 5px 0", fontSize: "20px" }}>
              {user.tradeName || user.name || user.firstName || "No Name"}
            </h3>
            {user.lastName && (
              <p style={{ margin: "5px 0", color: "#6b7280" }}>
                {user.lastName}
              </p>
            )}
          </div>

          {/* معلومات حساب Professional */}
          {user.userType?.toLowerCase() === "professional" && (
            <div style={{ 
              marginBottom: 20, 
              padding: "15px",
              background: "#f9fafb",
              borderRadius: 8,
              borderLeft: `4px solid ${user.accountType === "public" ? "#16a34a" : "#dc2626"}`
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ fontWeight: "500" }}>Account Type:</span>
                <span style={{ 
                  color: user.accountType === "public" ? "#16a34a" : "#dc2626",
                  fontWeight: "bold",
                  fontSize: "14px"
                }}>
                  {user.accountType === "public" ? "🌐 Public" : "🔒 Private"}
                </span>
              </div>
              <p style={{ fontSize: "13px", color: "#6b7280", margin: "10px 0" }}>
                {user.accountType === "public" 
                  ? "This account is visible to all clients in search results."
                  : "This account is private and not visible in search results."
                }
              </p>
              <button
                onClick={() => updateAccountType(user.accountType === "public" ? "privé" : "public")}
                disabled={updatingAccountType}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: user.accountType === "public" ? "#dc2626" : "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: "500",
                  marginTop: 10,
                  opacity: updatingAccountType ? 0.7 : 1
                }}
              >
                {updatingAccountType 
                  ? "Updating..." 
                  : user.accountType === "public" 
                    ? "Make Account Private" 
                    : "Make Account Public"
                }
              </button>
            </div>
          )}

          {/* زر تغيير الحالة */}
          <div style={{ 
            marginBottom: 20, 
            padding: "15px",
            background: "#f9fafb",
            borderRadius: 8
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: "500" }}>Account Status:</span>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={user.active !== false}
                  onChange={(e) => updateUserStatus(e.target.checked)}
                  style={{ 
                    marginRight: 8,
                    width: 18,
                    height: 18,
                    cursor: "pointer"
                  }}
                />
                <span style={{ 
                  color: user.active !== false ? "#16a34a" : "#ef4444",
                  fontWeight: "500"
                }}>
                  {user.active !== false ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
          </div>

          {/* معلومات الاتصال */}
          <div>
            <h4 style={{ 
              borderBottom: "2px solid #e5e7eb", 
              paddingBottom: 10,
              marginBottom: 15
            }}>
              Contact Information
            </h4>
            {user.email && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: 3 }}>
                  Email
                </div>
                <div style={{ fontWeight: "500", wordBreak: "break-all" }}>
                  {user.email}
                </div>
              </div>
            )}
            {user.phone && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: 3 }}>
                  Phone
                </div>
                <div style={{ fontWeight: "500" }}>
                  {user.phone}
                </div>
              </div>
            )}
            {user.adresse && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: 3 }}>
                  Address
                </div>
                <div style={{ fontWeight: "500" }}>
                  {user.adresse}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* التفاصيل الإضافية */}
        <div>
          {/* عرض الإحصائيات للـ Professional فقط */}
          {user.userType?.toLowerCase() === "professional" && (
            <div style={{ 
              background: "white", 
              padding: 25, 
              borderRadius: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              marginBottom: 20,
              borderLeft: `4px solid ${userTypeColor}`
            }}>
              <h3 style={{ 
                marginTop: 0, 
                marginBottom: 20,
                color: userTypeColor,
                display: "flex",
                alignItems: "center",
                gap: 10
              }}>
                <span>📊</span>
                Distributor Statistics
              </h3>
              
              <div style={{ 
                display: "flex", 
                gap: 15, 
                overflowX: "auto",
                paddingBottom: 10
              }}>
                <StatCard 
                  label="Products" 
                  value={stats.productsCount} 
                  icon="📦" 
                  color="#3b82f6" 
                />
                <StatCard 
                  label="Vendeurs" 
                  value={stats.vendeursCount} 
                  icon="👥" 
                  color="#10b981" 
                />
                <StatCard 
                  label="Categories" 
                  value={stats.categoriesCount} 
                  icon="📑" 
                  color="#f59e0b" 
                />
                <StatCard 
                  label="Brands" 
                  value={stats.brandsCount} 
                  icon="🏷️" 
                  color="#8b5cf6" 
                />
                <StatCard 
                  label="Orders" 
                  value={stats.ordersCount} 
                  icon="📋" 
                  color="#ef4444" 
                />
              </div>
            </div>
          )}

          {/* بطاقة التفاصيل حسب النوع */}
          <div style={{ 
            background: "white", 
            padding: 25, 
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            marginBottom: 20,
            borderLeft: `4px solid ${userTypeColor}`
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: 20,
              color: userTypeColor
            }}>
              {user.userType?.toUpperCase()} Details
            </h3>

            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", 
              gap: 20 
            }}>
              {userSpecificFields.map((field, index) => (
                <div key={index} style={{ minHeight: 60 }}>
                  <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: 5 }}>
                    {field.label}:
                  </div>
                  <div style={{ 
                    fontSize: "15px", 
                    fontWeight: "500",
                    minHeight: 24
                  }}>
                    {field.label === "Status" && field.editable ? field.value : field.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* بطاقة الإجراءات السريعة */}
          <div style={{ 
            background: "white", 
            padding: 25, 
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
          }}>
            <h4 style={{ marginTop: 0, marginBottom: 15 }}>Quick Actions</h4>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={() => navigate(`/users/${user.userType?.toLowerCase() || 'all'}`)}
                style={{
                  padding: "10px 20px",
                  background: userTypeColor,
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <span>👥</span>
                View All {user.userType}s
              </button>
              
              <button
                onClick={() => navigate("/users/all")}
                style={{
                  padding: "10px 20px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <span>📋</span>
                View All Users
              </button>
              
              {user.userType?.toLowerCase() === "professional" && (
                <button
                  onClick={() => {
                    window.open(`/professional-dashboard/${userId}`, '_blank');
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "#0891b2",
                    color: "white",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}
                >
                  <span>📊</span>
                  Open Distributor Dashboard
                </button>
              )}

               {user.userType?.toLowerCase() === "professional" && (
      <button
        onClick={() => navigate(`/admin/add-product/${userId}`)}
        style={{
          padding: "10px 20px",
          background: "#10b981",
          color: "white",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}
      >
        <span>➕</span>
        Add Product for Distributor
      </button>
    )}
              
              <button
                onClick={() => navigate("/")}
                style={{
                  padding: "10px 20px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                <span>🏠</span>
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* معلومات تقنية للتصحيح */}
      <div style={{ 
        background: "#f9fafb", 
        padding: "15px 20px", 
        borderRadius: 8,
        fontSize: "13px",
        color: "#6b7280",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div>
          <strong>User ID:</strong> <code>{userId}</code>
          {user.userType?.toLowerCase() === "professional" && (
            <span style={{ marginLeft: 20 }}>
              <strong>Distributor ID:</strong> <code>{userId}</code>
            </span>
          )}
        </div>
        <div>
          <strong>User Type:</strong> {user.userType || "Unknown"}
        </div>
        <div>
          <strong>Last Updated:</strong> {new Date().toLocaleTimeString()}
        </div>
      </div>
      
      {/* قسم التصحيح */}
      <details style={{ marginTop: 20, padding: 15, background: "#f3f4f6", borderRadius: 8 }}>
        <summary style={{ cursor: "pointer", fontWeight: "bold" }}>Debug Information</summary>
        <div style={{ marginTop: 10, fontSize: "12px" }}>
          <p><strong>User Data:</strong></p>
          <pre style={{ 
            background: "#1f2937", 
            color: "#f3f4f6", 
            padding: 10, 
            borderRadius: 4,
            overflow: "auto",
            maxHeight: 200
          }}>
            {JSON.stringify(user, null, 2)}
          </pre>
          <p><strong>Available Fields:</strong> {Object.keys(user).join(", ")}</p>
        </div>
      </details>
    </div>
  );
}